import { Visibility, VisibilityOff } from "@mui/icons-material";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import register from "./register.svg";

const SUBSCRIPTIONS = ["Simple Start", "Essentials", "Customised"];

const passwordRules = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One number", test: (p) => /[0-9]/.test(p) },
  { label: "One special character (@$!%*?&)", test: (p) => /[@$!%*?&]/.test(p) },
];

const SignUp = () => {
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);

  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [subscription, setSubscription] = useState("");
  const [otp, setOtp] = useState("");
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "" });

  const handleChange = (e, setter, field) => {
    setter(e.target.value);
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      setErrors((prev) => ({ ...prev, phone: "" }));
      setPhone(value);
    } else {
      setErrors((prev) => ({ ...prev, phone: "Only numbers are allowed" }));
    }
  };

  const validatePassword = (pwd) => {
    return passwordRules.every((rule) => rule.test(pwd));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!name) newErrors.name = "Name is required";
    if (!phone) newErrors.phone = "Phone number is required";
    if (phone && phone.length < 8) newErrors.phone = "Phone number must be at least 8 digits";
    if (!email) newErrors.email = "Email is required";
    if (!subscription) newErrors.subscription = "Please select a subscription plan";
    if (!password) newErrors.password = "Password is required";

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (email && !emailPattern.test(email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (password && !validatePassword(password)) {
      newErrors.password = "Password does not meet requirements";
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await fetch("/api/sendotpuser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, phone }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSnackbar({ open: true, message: data.error || "Failed to send OTP. Please try again.", severity: "error" });
        return;
      }

      setSnackbar({ open: true, message: "OTP sent to your email!", severity: "success" });
      setStep(2);
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred. Please try again.", severity: "error" });
    }
  };

  const handleVerifyOtpAndSignUp = async (e) => {
    e.preventDefault();
    if (!otp) {
      setErrors((prev) => ({ ...prev, otp: "OTP is required" }));
      return;
    }

    try {
      const response = await fetch("/api/signupUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, phone, email, password, subscription, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSnackbar({ open: true, message: data.error || "OTP verification failed. Please try again.", severity: "error" });
        return;
      }

      setSnackbar({ open: true, message: "Account created! Redirecting to login...", severity: "success" });
      setTimeout(() => navigate("/login"), 1500);
    } catch (error) {
      setSnackbar({ open: true, message: "An error occurred. Please try again.", severity: "error" });
    }
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Left side: Sign Up Form */}
      <div className="lg:w-1/2 bg-white flex items-center justify-center p-4">
        <form className="w-full max-w-sm px-6 py-8" onSubmit={(e) => e.preventDefault()}>
          <h2 className="text-3xl font-bold text-center mb-2">
            {step === 1 ? "Create Account" : "Verify OTP"}
          </h2>
          <p className="text-gray-500 text-center mb-6">
            {step === 1 ? "Sign up to get started with NexrBooks" : `We sent a code to ${email}`}
          </p>

          {step === 1 ? (
            <>
              {/* Name Field */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Full Name"
                  value={name}
                  onChange={(e) => handleChange(e, setName, "name")}
                  className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.name ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-sky-400`}
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>

              {/* Email Field */}
              <div className="mb-4">
                <input
                  type="email"
                  placeholder="Email Address"
                  value={email}
                  onChange={(e) => handleChange(e, setEmail, "email")}
                  className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.email ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-sky-400`}
                />
                {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
              </div>

              {/* Phone Field */}
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Phone Number"
                  value={phone}
                  onChange={handlePhoneChange}
                  className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.phone ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-sky-400`}
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>

              {/* Subscription Dropdown */}
              <div className="mb-4">
                <select
                  value={subscription}
                  onChange={(e) => handleChange(e, setSubscription, "subscription")}
                  className={`w-full px-4 py-3 rounded-lg bg-gray-100 border ${errors.subscription ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-sky-400 text-gray-700`}
                >
                  <option value="">Select Subscription Plan</option>
                  {SUBSCRIPTIONS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {errors.subscription && (
                  <p className="text-red-500 text-sm mt-1">{errors.subscription}</p>
                )}
              </div>

              {/* Password Field */}
              <div className="mb-1">
                <div className="relative">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => handleChange(e, setPassword, "password")}
                    className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.password ? "border-red-500" : "border-gray-300"
                      } focus:outline-none focus:ring-2 focus:ring-sky-400`}
                  />
                  <button
                    type="button"
                    onClick={() => setPasswordVisible(!passwordVisible)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 focus:outline-none"
                  >
                    {passwordVisible ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>

              {/* Password strength hints */}
              {password.length > 0 && (
                <ul className="mb-3 mt-2 space-y-1">
                  {passwordRules.map((rule, i) => (
                    <li key={i} className={`text-xs flex items-center gap-1 ${rule.test(password) ? "text-green-600" : "text-gray-400"}`}>
                      <span>{rule.test(password) ? "✓" : "○"}</span> {rule.label}
                    </li>
                  ))}
                </ul>
              )}

              {/* Confirm Password Field */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type={confirmPasswordVisible ? "text" : "password"}
                    placeholder="Confirm Password"
                    value={confirmPassword}
                    onChange={(e) => handleChange(e, setConfirmPassword, "confirmPassword")}
                    className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.confirmPassword ? "border-red-500" : confirmPassword && password === confirmPassword ? "border-green-400" : "border-gray-300"
                      } focus:outline-none focus:ring-2 focus:ring-sky-400`}
                  />
                  <button
                    type="button"
                    onClick={() => setConfirmPasswordVisible(!confirmPasswordVisible)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 focus:outline-none"
                  >
                    {confirmPasswordVisible ? <Visibility /> : <VisibilityOff />}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                )}
                {confirmPassword && password === confirmPassword && !errors.confirmPassword && (
                  <p className="text-green-600 text-sm mt-1">✓ Passwords match</p>
                )}
              </div>

              <button
                onClick={handleSendOtp}
                className="w-full px-8 py-3 bg-sky-500 text-white font-semibold rounded-full hover:bg-sky-600 transition focus:outline-none"
              >
                Send OTP
              </button>

              <p className="text-center text-gray-500 text-sm mt-4">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/login")}
                  className="text-sky-500 font-medium hover:underline"
                >
                  Sign In
                </button>
              </p>
            </>
          ) : (
            <>
              <div className="mb-4">
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => handleChange(e, setOtp, "otp")}
                  className={`w-full py-3 px-4 rounded-lg bg-gray-100 border ${errors.otp ? "border-red-500" : "border-gray-300"
                    } focus:outline-none focus:ring-2 focus:ring-sky-400 text-center text-xl tracking-widest`}
                  maxLength={6}
                />
                {errors.otp && <p className="text-red-500 text-sm mt-1">{errors.otp}</p>}
              </div>

              <button
                onClick={handleVerifyOtpAndSignUp}
                className="w-full px-8 py-3 bg-sky-500 text-white font-semibold rounded-full hover:bg-sky-600 transition focus:outline-none"
              >
                Verify & Create Account
              </button>

              <button
                type="button"
                onClick={() => setStep(1)}
                className="w-full mt-3 py-2 text-gray-500 hover:text-gray-700 text-sm transition"
              >
                ← Back to form
              </button>
            </>
          )}
        </form>
      </div>

      {/* Right side: Sign In Call to Action */}
      <div className="lg:w-1/2 bg-sky-500 flex flex-col items-center justify-center text-white p-8">
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">NexrBooks</div>
          <p className="text-sky-100 mb-4 text-lg">Accounting and HR Made Easy</p>
          <h3 className="text-2xl font-bold">Already one of us?</h3>
          <p className="mt-4">
            Sign In and continue where you left off. We are glad to have you back!
          </p>
          <button
            className="px-6 py-2 border-2 border-white rounded-full hover:bg-white hover:text-sky-500 transition mt-4"
            onClick={() => navigate("/login")}
          >
            Sign In
          </button>
        </div>
        <img
          src={register}
          className="hidden lg:block w-4/5 max-w-md mt-16"
          alt="Register illustration"
        />
      </div>
    </div>
  );
};

export default SignUp;
